/*
 * Generates the table of problems from data retrieved from the API.
 */

import React from "react";
import { Change } from "../pages/ChangesPage";
import { dateDisplay, companyDisplay, agentDisplay, DATE_FORMAT, personDisplay } from "../helpers/dataHelper";
import MUIDataTable from "mui-datatables";
import moment from "moment";
import clsx from "clsx";
import { Typography } from "@material-ui/core";
import useRowStyles from "../hooks/useRowStyles";

interface ChangesTableProps {
    changes: Change[];
    paging: {
        page: number;
        perPage: number;
        total: number;
        setPage: (page: number) => void;
        setRowsPerPage: (numberOfRows: number) => void;
    };
    sorting: {
        sortProperty: string;
        sortDirection: number;
        setSort: (property: string) => void;
    };
}

const ChangesTable: React.FC<ChangesTableProps> = ({ changes, paging, sorting }) => {
    const rowClasses = useRowStyles();

    const columns = [
        {
            label: "Change ID",
            name: "id",
            options: {
                customHeadLabelRender: (columnMeta: any) => {
                    return (
                        <Typography component={"span"} noWrap={true} variant="body2">
                            {columnMeta.label}
                        </Typography>
                    );
                }
            }
        },
        {
            label: "Company Name",
            name: "client",
            options: {
                customBodyRenderLite: (dataIndex: number) => {
                    const change = changes[dataIndex];
                    return companyDisplay(change.client);
                }
            }
        },
        {
            label: "Subject",
            name: "subject"
        },
        {
            label: "Requester",
            name: "requester",
            options: {
                customBodyRender: (value: any) => {
                    return personDisplay(value);
                }
            }
        },
        {
            label: "Status",
            name: "status",
            options: {
                customBodyRender: (value: any) => {
                    return (
                        <Typography component={"span"} noWrap={true} variant="body2">
                            {value}
                        </Typography>
                    );
                }
            }
        },
        {
            label: "Impact",
            name: "impact"
        },
        {
            label: "Risk",
            name: "risk"
        },
        {
            label: "Agent",
            name: "agent",
            options: {
                customBodyRender: (value: any) => {
                    return (
                        <Typography component={"span"} noWrap={true} variant="body2">
                            {agentDisplay(value)}
                        </Typography>
                    );
                }
            }
        },
        {
            label: "Planned Start",
            name: "plannedStartDate",
            options: {
                customBodyRender: (value: any) => dateDisplay(value)
            }
        },
        {
            label: "Planned End",
            name: "plannedEndDate",
            options: {
                customBodyRender: (value: any) => dateDisplay(value)
            }
        },
        {
            label: "URL",
            name: "url",
            options: {
                display: false
            }
        }
    ];

    return (
        <MUIDataTable
            title=""
            data={changes}
            columns={columns}
            options={{
                sortOrder: {
                    name: sorting.sortProperty,
                    direction: sorting.sortDirection === -1 ? "desc" : "asc"
                },
                viewColumns: false,
                print: false,
                filter: false,
                search: false,
                selectableRows: "none",
                serverSide: true,
                page: paging.page,
                rowsPerPage: paging.perPage ?? 30,
                count: paging.total,
                download: false,
                onColumnSortChange: (changedColumn: string) => {
                    sorting.setSort(changedColumn);
                },
                onChangeRowsPerPage: (numberOfRows: number) => {
                    paging.setRowsPerPage(numberOfRows);
                },
                onChangePage: (currentPage: number) => {
                    paging.setPage(currentPage);
                },
                setTableProps: () => {
                    return {
                        size: "small"
                    };
                },
                setRowProps: (row: any[]) => {
                    const overdueStatus = () => {
                        const timeToDueDate = moment.utc(row[9], DATE_FORMAT).diff(moment.utc());
                        if (timeToDueDate < 0) {
                            return "overdue";
                        } else if (timeToDueDate < 7200000) {
                            // 2 hours
                            return "close";
                        }

                        return "ok";
                    };

                    return {
                        className: clsx(
                            overdueStatus() === "overdue" && rowClasses.overdue,
                            overdueStatus() === "close" && rowClasses.close,
                            overdueStatus() === "ok" && rowClasses.standard
                        )
                    };
                },
                onRowClick: (rowData: string[]) => {
                    window.open(`${rowData[10]}`);
                },
                rowsPerPageOptions: [30, 100, 300]
            }}
        />
    );
};

export default ChangesTable;
